/** Report-local pseudonyms: preserve path shape, never a reusable artist alias. */
const commonFolders = new Set([
  'users',
  'home',
  'volumes',
  'library',
  'cloudstorage',
  'onedrive',
  'onedrive-personal',
  'documents',
  'desktop',
  'downloads',
  'music',
  'pictures',
  'videos',
  'public',
  'appdata',
  'local',
  'roaming',
  'application support',
  'temp',
  'tmp',
  'private',
  'var',
  'folders',
  'backup',
  'backups',
  'samples',
  'renders',
  'ableton project info',
  'contents',
  'dropbox',
  'google drive',
]);
const knownExtensions =
  '(?:als|alp|asd|adg|adv|fxp|fxb|vstpreset|serumpreset|vital|phaseplant|multipass|snapheap|fst|nki|nkm|nksf|nksfx|h2p|wav|aiff?|mp3|flac|ogg|m4a|mid|midi|flp|logicx|rpp|rpp-bak|reapeaks|ptx|cpr|npr|song|bwproject|band|peak|pkf|json|xml|txt|zip|7z|bin|png|jpe?g|webp|pdf)';
const extensions = new RegExp(`\\.${knownExtensions}(?=\\s*$)`, 'i');
const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Secrets are removed, not pseudonymized. Paths are handled separately. */
export function redactSecrets(value: string): string {
  return value
    .replace(/https?:\/\/[^\s<>"']+/gi, '[URL redacted]')
    .replace(/\bBearer\s+\S+/gi, 'Bearer [redacted]')
    .replace(/\beyJ[\w-]+\.[\w-]+\.[\w-]+\b/g, '[token redacted]')
    .replace(/\b(?:AKIA|ASIA)[A-Z0-9]{16}\b/g, '[key redacted]')
    .replace(
      /\b(?:token|accessToken|refreshToken|password|secret|authorization|cookie|x-amz-signature)["']?\s*[:=]\s*(?:"[^"]*"|'[^']*'|[^\s,;]+)/gi,
      '[credential redacted]'
    )
    .replace(/[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi, '[email redacted]');
}

// GraphQL validation errors echo entire input objects, including notes and
// filenames. Remove that value using balanced delimiters, not a regex ending at
// the first semicolon (a private note can itself contain semicolons/braces).
function redactInputEchoes(value: string): string {
  const match = /\bgot invalid value\s+/.exec(value);
  if (!match) return value;
  const prefix = value.slice(0, match.index + match[0].length);
  const input = value.slice(prefix.length);
  let quote = '';
  let depth = 0;
  let escaped = false;
  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    if (quote) {
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === quote) quote = '';
    } else if (char === '"' || char === "'") quote = char;
    else if (char === '{' || char === '[') depth++;
    else if (char === '}' || char === ']') depth--;
    if (!quote && depth === 0 && (char === ';' || char === '\n')) {
      return `${prefix}[input values redacted]${redactInputEchoes(input.slice(i))}`;
    }
  }
  return `${prefix}[input values redacted]`;
}

export function createDiagnosticMasker() {
  const names = new Map<string, string>();
  const used = new Set<string>();
  let cursor = 0;
  const random = new Uint32Array(256);
  crypto.getRandomValues(random);
  const choose = (values: string[], original: string) => {
    const candidates = values.filter((value) => value !== original);
    return candidates[
      (random[cursor++ % random.length] + cursor) % candidates.length
    ];
  };
  const scramble = (value: string) =>
    Array.from(value, (char) => {
      if (/[A-Z]/.test(char))
        return choose(Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ'), char);
      if (/[a-z]/.test(char))
        return choose(Array.from('abcdefghijklmnopqrstuvwxyz'), char);
      if (/[0-9]/.test(char)) return choose(Array.from('0123456789'), char);
      if (!/[\p{L}\p{N}]/u.test(char)) return char; // Keep spaces, marks, symbols and punctuation.
      // Preserve Unicode script/block and UTF-16 length for path-encoding bugs.
      const start = char.codePointAt(0)! & ~127;
      const category = /\p{L}/u.test(char) ? /\p{L}/u : /\p{N}/u;
      const alphabet = Array.from({ length: 128 }, (_, i) =>
        String.fromCodePoint(start + i)
      ).filter(
        (candidate) =>
          category.test(candidate) && candidate.length === char.length
      );
      return alphabet.length > 1 ? choose(alphabet, char) : 'x';
    }).join('');
  const name = (value: string): string => {
    if (names.has(value)) return names.get(value)!;
    let masked = scramble(value);
    for (let attempt = 0; used.has(masked) && attempt < 64; attempt++)
      masked = scramble(value);
    names.set(value, masked);
    used.add(masked);
    return masked;
  };
  const path = (value: string, maskLeaf = false): string =>
    value
      .split(/([\\/])/)
      .map((part, index, parts) => {
        const previous = parts
          .slice(0, index)
          .filter((item) => item && !/^[\\/]$/.test(item))
          .pop()
          ?.toLowerCase();
        // A username does not become safe because it is also a common folder word.
        const isLeaf = !parts
          .slice(index + 1)
          .some((item) => item && !/^[\\/]$/.test(item));
        if (
          previous === 'users' ||
          previous === 'home' ||
          (maskLeaf && isLeaf && commonFolders.has(part.toLowerCase()))
        )
          return name(part);
        if (
          !part ||
          /^[\\/]$/.test(part) ||
          commonFolders.has(part.toLowerCase()) ||
          ['.', '..', '.DS_Store', '.gitkeep'].includes(part)
        )
          return part;
        // Preserve recognizable provider labels, but not the account/team suffix.
        const provider = /^(OneDrive|Dropbox|Google Drive)( - |-| \()/i.exec(
          part
        );
        if (provider) return provider[0] + name(part.slice(provider[0].length));
        const extension = extensions.exec(part);
        return extension
          ? name(part.slice(0, extension.index)) + part.slice(extension.index)
          : name(part);
      })
      .join('');

  const known = new Map<string, string>();
  const remember = (value: string | undefined, isPath = false) => {
    if (value) known.set(value, isPath ? path(value, true) : name(value));
  };
  const text = (source: string) => {
    const protectedValues: string[] = [];
    const protect = (value: string) => {
      const expanded = value.replace(
        /\uE000(\d+)\uE001/g,
        (_, index: string) => protectedValues[Number(index)]
      );
      return `\uE000${protectedValues.push(expanded) - 1}\uE001`;
    };
    // Do not allow literal input to impersonate our private replacement markers.
    let result = redactSecrets(
      redactInputEchoes(source.replace(/[\uE000-\uE003]/g, ''))
    );
    // Legacy upload errors use an unquoted path followed by a sentence. Treat
    // only the filename as private, not the extension plus the recovery text.
    result = result.replace(
      /(Could not read )([^\r\n]+?)(\. No version was created\.)/g,
      (_, prefix: string, filename: string, suffix: string) => {
        if (/^["']/.test(filename)) return prefix + filename + suffix;
        return (
          prefix + protect(known.get(filename) ?? path(filename, true)) + suffix
        );
      }
    );
    if (known.size) {
      const pattern = [...known.keys()]
        .sort((a, b) => b.length - a.length)
        .map(escapeRegExp)
        .join('|');
      result = result.replace(
        new RegExp(
          `(?<![\\p{L}\\p{N}_])(?:${pattern})(?![\\p{L}\\p{N}_])`,
          'gu'
        ),
        (value) => protect(known.get(value)!)
      );
    }
    // Keep quoted schema identifiers useful, but hide other quoted user values.
    result = result.replace(
      /\b(?:Field|field|type|argument|Variable|variable)\s+"[^"\r\n]+"/g,
      protect
    );
    result = result.replace(
      /(["'])([^"'\r\n]*)\1/g,
      (whole, quote: string, content: string) => {
        if (/[\uE000\uE001]/.test(content)) return whole;
        return `${quote}${protect(path(content, !/[\\/]/.test(content)))}${quote}`;
      }
    );
    result = result.replace(
      /(?:file:\/\/)?(?:[A-Za-z]:[\\/]|\\\\|\/(?!\s))[^\r\n<>"'\uE000\uE001]+/g,
      (value) => protect(path(value))
    );
    // Unquoted relative paths at the end of filesystem/checksum error messages.
    result = result.replace(
      /(?<![\p{L}\p{N}])(?:[\p{L}\p{N}_.-]+[\\/])[^\r\n<>"'\uE000\uE001]+/gu,
      (value) => protect(path(value))
    );
    // Early failures may precede file discovery, so no metadata is registered.
    // Preserve the filesystem reason while masking its unquoted filename too.
    result = result.replace(
      new RegExp(
        `(\\b(?:(?:Could not|Cannot|Unable to|Failed to) (?:read|write|open|upload|download|delete|sync|access)|(?:file|filename|path):)\\s+)([^\\r\\n<>"'\\uE000\\uE001]+?\\.${knownExtensions})(?=$|[\\s.,;:)])`,
        'gi'
      ),
      (_, prefix: string, filename: string) =>
        prefix + protect(path(filename, true))
    );
    return result.replace(
      /\uE000(\d+)\uE001/g,
      (_, index: string) => protectedValues[Number(index)]
    );
  };
  return { name, path, remember, text };
}
